import { useIsAuthenticated, useMsal } from '@azure/msal-react';

export const useAuth = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const getAccessToken = async (forceInteractive = false) => {
    if (!isAuthenticated || accounts.length === 0) {
      throw new Error('User not authenticated');
    }

    try {
      // First attempt: silent token acquisition
      if (!forceInteractive) {
        const response = await instance.acquireTokenSilent({
          scopes: ['User.Read'],
          account: accounts[0]
        });
        return response.accessToken;
      } else {
        // If forced interactive, skip silent and go straight to interactive
        throw new Error('FORCE_INTERACTIVE');
      }
    } catch (error) {
      // Check if this is an interaction_required error (AADSTS errors)
      const isInteractionRequired = 
        error.errorCode === 'interaction_required' ||
        error.errorMessage?.includes('AADSTS') ||
        error.errorMessage?.includes('interaction_required') ||
        error.message === 'FORCE_INTERACTIVE';
      
      if (isInteractionRequired) {
        console.log('Token acquisition requires interaction - attempting interactive login');
        try {
          // Attempt interactive token acquisition (popup)
          const response = await instance.acquireTokenPopup({
            scopes: ['User.Read'],
            account: accounts[0],
            prompt: 'none' // Try to acquire token without prompting if possible
          });
          return response.accessToken;
        } catch (interactiveError) {
          console.error('Interactive token acquisition failed:', interactiveError);
          throw new Error('AUTHENTICATION_REQUIRED');
        }
      }
      
      console.error('Failed to acquire token:', error);
      throw error;
    }
  };

  const getUserEmail = () => {
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      
      return account.idTokenClaims?.email ||
             account.idTokenClaims?.preferred_username ||
             account.username ||
             account.idTokenClaims?.upn ||
             '';
    }
    return '';
  };

  const getUserDisplayName = () => {
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      
      return account.idTokenClaims?.name ||
             (account.idTokenClaims?.given_name && account.idTokenClaims?.family_name 
               ? `${account.idTokenClaims.given_name} ${account.idTokenClaims.family_name}`
               : null) ||
             account.name ||
             account.idTokenClaims?.preferred_username ||
             account.username ||
             'User';
    }
    return 'User';
  };

  // Get OIDC standard 'sub' claim (stable unique identifier)
  const getUserSubjectId = () => {
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      return account.idTokenClaims?.sub || account.idTokenClaims?.oid || null;
    }
    return null;
  };

  // Get OIDC standard identity provider claim
  const getIdentityProvider = () => {
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      
      // Check various provider claims that different IdPs use
      const idp = account.idTokenClaims?.idp || 
                  account.idTokenClaims?.iss || 
                  account.idTokenClaims?.issuer ||
                  account.idTokenClaims?.tfp;
      
      // Normalize common providers to friendly names
      if (!idp) return 'entra-work'; // Default for Entra ID
      
      if (idp.includes('live.com') || idp.includes('microsoft.com')) return 'live.com';
      if (idp.includes('google')) return 'google';
      if (idp.includes('facebook')) return 'facebook';
      if (idp.includes('ciamlogin.com') || idp.includes('b2clogin.com')) return 'entra-external';
      if (idp.includes('login.microsoftonline.com')) return 'entra-work';
      
      return idp; // Return raw value if no match
    }
    return 'entra-work'; // Default
  };

  // Get all OIDC identity claims for user/admin creation
  const getOIDCIdentityClaims = () => {
    return {
      subjectId: getUserSubjectId(),
      identityProvider: getIdentityProvider(),
      email: getUserEmail()
    };
  };

  return {
    isAuthenticated,
    accounts,
    getAccessToken,
    getUserEmail,
    getUserDisplayName,
    getUserSubjectId,
    getIdentityProvider,
    getOIDCIdentityClaims
  };
};